import styled, { css } from "styled-components";
import { rawColors } from "@magicbi/ui-library";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import type { TooltipPosition, TooltipVariant } from "./Tooltip.types";

// ─── Variant color map ────────────────────────────────────────────────────────
// Add a new entry here to create a new fixed variant.
// Every variant needs bg (bubble background) and text (label color).
// Optional: border (for light variant).

const VARIANT_STYLES: Record<
  TooltipVariant,
  { bg: string; text: string; border?: string }
> = {
  /** Deep navy — matches the Figma canvas control panel spec. */
  dark: {
    bg:   rawColors.canvas.tooltipDark,   // #1B2D4A
    text: rawColors.structural.white,
  },
  /** Brand primary blue — for action buttons / CTAs. */
  primary: {
    bg:   rawColors.primary[500],         // #1656CF
    text: rawColors.structural.white,
  },
  /** White bubble with a grey border — for light surfaces / form fields. */
  light: {
    bg:     rawColors.structural.white,
    text:   rawColors.text.highEmphasis,
    border: rawColors.grey[200],
  },
};

// ─── Constants ────────────────────────────────────────────────────────────────
const RADIUS = "8px";
const ARROW  = "5px";    // triangle half-size
const OFFSET = "10px";   // gap between trigger edge and bubble

// ─── Arrow per position (color injected at runtime from variant bg) ───────────
const makeArrow = (
  position: TooltipPosition,
  bg: string,
): ReturnType<typeof css> => {
  const arrowMap: Record<TooltipPosition, ReturnType<typeof css>> = {
    left: css`
      &::after {
        content: "";
        position: absolute;
        right: -${ARROW};
        top: 50%;
        transform: translateY(-50%);
        border-width: ${ARROW} 0 ${ARROW} ${ARROW};
        border-style: solid;
        border-color: transparent transparent transparent ${bg};
      }
    `,
    right: css`
      &::after {
        content: "";
        position: absolute;
        left: -${ARROW};
        top: 50%;
        transform: translateY(-50%);
        border-width: ${ARROW} ${ARROW} ${ARROW} 0;
        border-style: solid;
        border-color: transparent ${bg} transparent transparent;
      }
    `,
    top: css`
      &::after {
        content: "";
        position: absolute;
        bottom: -${ARROW};
        left: 50%;
        transform: translateX(-50%);
        border-width: ${ARROW} ${ARROW} 0 ${ARROW};
        border-style: solid;
        border-color: ${bg} transparent transparent transparent;
      }
    `,
    bottom: css`
      &::after {
        content: "";
        position: absolute;
        top: -${ARROW};
        left: 50%;
        transform: translateX(-50%);
        border-width: 0 ${ARROW} ${ARROW} ${ARROW};
        border-style: solid;
        border-color: transparent transparent ${bg} transparent;
      }
    `,
  };
  return arrowMap[position];
};

// ─── Bubble placement per position ───────────────────────────────────────────
const PLACEMENT: Record<TooltipPosition, ReturnType<typeof css>> = {
  left: css`
    right: calc(100% + ${OFFSET});
    top: 50%;
    transform: translateY(-50%);
  `,
  right: css`
    left: calc(100% + ${OFFSET});
    top: 50%;
    transform: translateY(-50%);
  `,
  top: css`
    bottom: calc(100% + ${OFFSET});
    left: 50%;
    transform: translateX(-50%);
  `,
  bottom: css`
    top: calc(100% + ${OFFSET});
    left: 50%;
    transform: translateX(-50%);
  `,
};

// ─── Styled components ────────────────────────────────────────────────────────

export const Bubble = styled.span<{
  $position: TooltipPosition;
  $variant: TooltipVariant;
  $multiline?: boolean;
  $maxWidth?: string;
  $arrow?: boolean;
}>`
  position: absolute;
  z-index: 9999;
  pointer-events: none;
  white-space: ${({ $multiline }) => ($multiline ? "normal" : "nowrap")};
  ${({ $multiline, $maxWidth }) =>
    $multiline
      ? css`
          width: ${$maxWidth ?? "280px"};
          min-width: ${$maxWidth ?? "280px"};
          max-width: ${$maxWidth ?? "280px"};
          overflow-wrap: break-word;
          word-break: normal;
          box-sizing: border-box;
        `
      : ""}
  ${typographyMixin("caption", 2)}
  min-height: 28px;
  box-sizing: border-box;
  display: ${({ $multiline }) => ($multiline ? "block" : "inline-flex")};
  align-items: center;
  padding: ${spacing[1.5]} ${spacing[2.5]};
  border-radius: ${RADIUS};

  /* ── Variant colors ── */
  background: ${({ $variant }) => VARIANT_STYLES[$variant].bg};
  color:      ${({ $variant }) => VARIANT_STYLES[$variant].text};
  ${({ $variant }) =>
    VARIANT_STYLES[$variant].border
      ? css`border: 1px solid ${VARIANT_STYLES[$variant].border};`
      : ""}

  /* ── Hidden by default ── */
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease, visibility 0.15s ease;

  /* ── Placement ── */
  ${({ $position }) => PLACEMENT[$position]}

  /* ── Arrow (color must match bubble bg) ── */
  ${({ $position, $variant, $arrow = true }) => ($arrow ? makeArrow($position, VARIANT_STYLES[$variant].bg) : "")}
`;

/** A mark after the label (shortcut, "Coming soon" tag), set apart from it. */
export const Trailing = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
  vertical-align: middle;
`;

/** The quieter text after a tooltip's label (a shortcut or a note). Renders inside `Trailing`. */
export const Hint = styled.span`
  font-size: 0.6875rem; /* GAP: 11px, no token */
  color: ${rawColors.primary.lavender};
`;

export const Wrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: visible;

  &:hover ${Bubble},
  &:focus-within ${Bubble} {
    opacity: 1;
    visibility: visible;
  }
`;
